import classNames from "classnames";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useEffectOnce } from "usehooks-ts";
import ModalProvider from "../../../providers/ModalProvider";
import WalletConnectorProvider from "../../../providers/WalletConnectorProvider";
import { AppPage, AppRefreshStatus } from "../../../types";
import {
  selectRefreshPage,
  selectRefreshStatus,
} from "../../app/app.selectors";
import { appActions } from "../../app/app.state";
import TokenTransferModalProvider from "../providers/TokenTransferModalProvider";
import TreasuryUiProvider, {
  useTreasuryUiContext,
} from "../providers/TreasuryUiContext";
import {
  getAccountBalancesActionsAction,
  getMmLatestTradesAction,
  getMmOrderBookAction,
  getRwaContractBalancesAction,
  getTreasuryTransactionsAction,
} from "../treasury.effects";
import {
  selectIsGetAccountBalancesPending,
  selectIsGetMmLatestTradesPending,
  selectIsGetMmOrderBookPending,
  selectIsGetRwaContractBalancesPending,
  selectIsGetTreasuryTransactionsPending,
} from "../treasury.selectors";
import AccountBalances from "./AccountBalances";
import RwaContractBalances from "./RwaContractBalances";
import "./Treasury.scss";
import TreasuryTransactions from "./TreasuryTransactions";
import { selectTreasuryTransactionsSelectedCategory } from "../treasury.selectors";
import { TreasuryTransactionCategory } from "../types/treasury-transactions-category.type";
import { CONSTANTS } from "../../../common/constants";

const Treasury = () => {
  const selectedCategory = useSelector(
    selectTreasuryTransactionsSelectedCategory
  );
  const { isTxWidgetsExpanded } = useTreasuryUiContext();
  const refreshPage = useSelector(selectRefreshPage);
  const refreshStatus = useSelector(selectRefreshStatus);
  const isGetAccountBalancesPending = useSelector(
    selectIsGetAccountBalancesPending
  );
  const isGetRwaContractBalancesPending = useSelector(
    selectIsGetRwaContractBalancesPending
  );
  const isGetTreasuryTransactionsPending = useSelector(
    selectIsGetTreasuryTransactionsPending
  );
  const isGetMmOrderBookPending = useSelector(selectIsGetMmOrderBookPending);
  const isGetMmLatestTradesPending = useSelector(
    selectIsGetMmLatestTradesPending
  );
  const dispatch = useDispatch();
  const firstRender = useRef(true);

  const getTreasuryData = () => {
    dispatch(getRwaContractBalancesAction());
    dispatch(getAccountBalancesActionsAction());
    if (!firstRender.current) {
      switch (selectedCategory) {
        case TreasuryTransactionCategory.CexOrders:
          dispatch(getMmOrderBookAction(CONSTANTS.MarketMaker.Exim));
          break;

        case TreasuryTransactionCategory.CexLatestTrades:
          dispatch(getMmLatestTradesAction(CONSTANTS.MarketMaker.Exim));
          break;

        default:
          dispatch(getTreasuryTransactionsAction({}));
          break;
      }
    }
  };

  useEffect(() => {
    if (
      refreshStatus === AppRefreshStatus.Invalidated &&
      refreshPage === AppPage.Treasury
    ) {
      getTreasuryData();
    }
  }, [refreshStatus, refreshPage]);

  useEffect(() => {
    let status = AppRefreshStatus.Idle;
    if (
      isGetRwaContractBalancesPending ||
      isGetAccountBalancesPending ||
      isGetTreasuryTransactionsPending ||
      isGetMmOrderBookPending ||
      isGetMmLatestTradesPending
    ) {
      status = AppRefreshStatus.InProgress;
    }
    if (refreshStatus !== status) {
      dispatch(
        appActions.changeRefresh({ page: AppPage.Treasury, status: status })
      );
    }
  }, [
    isGetRwaContractBalancesPending,
    isGetAccountBalancesPending,
    isGetTreasuryTransactionsPending,
    isGetMmOrderBookPending,
    isGetMmLatestTradesPending,
    refreshStatus,
  ]);

  useEffectOnce(() => {
    getTreasuryData();
    firstRender.current = false;
  });

  return (
    <WalletConnectorProvider>
      <ModalProvider>
        <TokenTransferModalProvider>
          <div className="TreasuryManagement">
            <div
              className={classNames("Widget col-span-2 transition-all", {
                expanded: isTxWidgetsExpanded,
              })}
            >
              <TreasuryTransactions />
            </div>

            <div className="Widget col-span-2">
              <RwaContractBalances />
            </div>

            <div className="Widget opacity-0" />

            <div className="Widget">
              <AccountBalances />
            </div>
          </div>
        </TokenTransferModalProvider>
      </ModalProvider>
    </WalletConnectorProvider>
  );
};

export default () => {
  return (
    <TreasuryUiProvider>
      <Treasury />
    </TreasuryUiProvider>
  );
};
